import { useEffect, useRef } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useTheme } from '../theme';
import { AutoFillButton } from './AutoFill';
import { Icon } from './icons';

/** Logo en trois calques (encre, okoumé, feuille) qui suit le mode jour / nuit. */
export function Logo({ width, label = 'CASIJUD' }: { width: number | string; label?: string }) {
  return (
    <span className="logo" role="img" aria-label={label} style={{ width }}>
      <i className="la" />
      <i className="lb" />
      <i className="lc" />
    </span>
  );
}

/** Liseré aux couleurs du drapeau : sous l'en-tête uniquement. */
export function Flag() {
  return (
    <div className="flag" aria-hidden="true">
      <i style={{ background: '#009E60' }} />
      <i style={{ background: '#FCD116' }} />
      <i style={{ background: '#3A75C4' }} />
    </div>
  );
}

export function ThemeToggle() {
  const [theme, toggle] = useTheme();
  return (
    <button
      className="ib"
      type="button"
      onClick={toggle}
      aria-pressed={theme === 'dark'}
      aria-label={theme === 'dark' ? 'Passer en mode jour' : 'Passer en mode nuit'}
      title={theme === 'dark' ? 'Mode jour' : 'Mode nuit'}
    >
      <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={18} />
    </button>
  );
}

/**
 * Sur téléphone, les tableaux deviennent des cartes : chaque cellule reçoit le titre de sa colonne (data-l),
 * lu dans l'en-tête du tableau. Fait une fois pour toutes les pages, à chaque changement du contenu.
 */
function useResponsiveTables(root: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const label = () => {
      el.querySelectorAll('table').forEach((table) => {
        const heads = [...table.querySelectorAll('thead th')].map((th) => th.textContent?.trim() ?? '');
        if (heads.length === 0) return;
        table.querySelectorAll('tbody tr').forEach((tr) => {
          [...tr.children].forEach((td, i) => {
            if (td.tagName === 'TD' && heads[i] && td.getAttribute('data-l') !== heads[i]) {
              td.setAttribute('data-l', heads[i]);
            }
          });
        });
      });
    };
    label();
    const observer = new MutationObserver(label);
    observer.observe(el, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [root]);
}

export function Layout() {
  const { status, logout } = useAuth();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const menuRef = useRef<HTMLDetailsElement>(null);
  useResponsiveTables(mainRef);

  useEffect(() => {
    if (menuRef.current) menuRef.current.open = false;
  }, [location.pathname]);

  const inWorkspace = location.pathname.startsWith('/espace');
  const fullWidth = inWorkspace || location.pathname === '/';
  const authenticated = status === 'authenticated';

  return (
    <>
      <a className="skip-link" href="#contenu">
        Aller au contenu
      </a>
      <header className="nav">
        <div className="navin">
          <Link to="/" className="brand" aria-label="CASIJUD - accueil">
            <Logo width={46} label="Logo CASIJUD" />
            <span>
              <b>CASIJUD</b>
              <small>Casier judiciaire numérique du Gabon</small>
            </span>
          </Link>
          <nav className="links" aria-label="Navigation principale">
            <NavLink to="/demarches">Démarches</NavLink>
            <NavLink to="/suivi">Suivre une demande</NavLink>
            <NavLink to="/verifier">Vérifier un document</NavLink>
            {authenticated && <NavLink to="/espace">Mon espace</NavLink>}
          </nav>
          <div className="tools">
            <ThemeToggle />
            {authenticated ? (
              <button className="btn btn-ghost btn-sm hide-s" type="button" onClick={() => void logout()}>
                Se déconnecter
              </button>
            ) : (
              <Link className="btn btn-sm" to="/connexion">
                Se connecter
              </Link>
            )}
            <details className="menu" ref={menuRef}>
              <summary className="ib" aria-label="Menu">
                <Icon name="menu" size={20} />
              </summary>
              <div className="pop">
                <Link to="/">Accueil</Link>
                <Link to="/demarches">Démarches</Link>
                <Link to="/suivi">Suivre une demande</Link>
                <Link to="/verifier">Vérifier un document</Link>
                {authenticated ? (
                  <>
                    <Link to="/espace">Mon espace</Link>
                    <button type="button" onClick={() => void logout()}>
                      Se déconnecter
                    </button>
                  </>
                ) : null}
              </div>
            </details>
          </div>
        </div>
      </header>
      <Flag />
      <main id="contenu" tabIndex={-1} ref={mainRef} className={fullWidth ? undefined : 'page'}>
        <Outlet />
      </main>
      <AutoFillButton root={mainRef} />
      <footer className="foot">
        <div>
          <span>CASIJUD - proposition d’Infinity Digital Gabon au ministère de la Justice.</span>
          <span>Aucune donnée judiciaire n’est consultable publiquement.</span>
        </div>
      </footer>
    </>
  );
}
